/**
 * Audit log — design/PROTOTYPE-SPEC.md §10: "Append-only record of all actions,
 * filterable by person/category/date range. Cannot edit or delete."
 *
 * ## Why this reads differently from Activity
 *
 * Activity is the stock ledger: what moved, when, how much. This is everything
 * *else* — who changed the plan, the VAT rate, someone's role, who signed in.
 * They deliberately stay separate pages rather than one merged feed, because
 * the questions are different. "Where did that vial go" and "who put us on
 * Premium" are asked by different people at different times.
 *
 * There is no edit and no delete, and the absence is the feature. The database
 * enforces it (`packages/db/sql/append-only.sql`), so a button here would fail
 * even if someone added one.
 */
import {
  VARIANCE_REASON_LABEL,
  AUDIT_CATEGORIES,
  AUDIT_CATEGORY_LABEL,
  type AuditCategory,
} from '@inventory-tracker/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudit, getTeam, type AuditEntry, type TeamMember } from '../lib/api.js';
import { c, sans } from '../theme.js';
import { cardStyle, humaniseReason, smallButton } from './format.js';

interface Props {
  /** Bumped by the shell after any write, so the log reflects it. */
  refreshToken: number;
}

interface Filters {
  category: string;
  userId: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { category: '', userId: '', from: '', to: '' };

export function AuditPage({ refreshToken }: Props) {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [people, setPeople] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** First page. Replaces what is on screen, unlike "Load more". */
  const load = useCallback(async (f: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAudit({ ...f });
      setEntries(res.entries);
      setCursor(res.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * The effect below must reload with whatever is currently filtered, but must
   * not RE-RUN when the filter changes — `apply()` already loads on change, so
   * depending on `filters` would fire a second request for every change. A ref
   * gives the effect the current value without making it a dependency.
   */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    void load(filtersRef.current);
  }, [load, refreshToken]);

  useEffect(() => {
    // Populates the "person" filter. Failing is not fatal — the rest of the
    // page works, the dropdown just falls back to "Everyone".
    void getTeam()
      .then((t) => setPeople(t.members ?? []))
      .catch(() => undefined);
  }, []);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await getAudit({ ...filters, cursor });
      // Appended, never merged by id: the cursor guarantees these are strictly
      // older than what is already on screen.
      setEntries((prev) => [...prev, ...res.entries]);
      setCursor(res.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  }

  function apply(next: Partial<Filters>) {
    const merged = { ...filters, ...next };
    setFilters(merged);
    void load(merged);
  }

  const filtered = Object.values(filters).some((v) => v !== '');

  return (
    <div data-tour="audit">
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Select
            id="auditCategory"
            label="Category"
            value={filters.category}
            onChange={(v) => apply({ category: v })}
            options={[
              { value: '', label: 'All categories' },
              ...AUDIT_CATEGORIES.map((k) => ({
                value: k,
                label: AUDIT_CATEGORY_LABEL[k as AuditCategory],
              })),
            ]}
          />
          <Select
            id="auditPerson"
            label="Person"
            value={filters.userId}
            onChange={(v) => apply({ userId: v })}
            options={[
              { value: '', label: 'Everyone' },
              ...people.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          <DateBox id="auditFrom" label="From" value={filters.from} onChange={(v) => apply({ from: v })} />
          <DateBox id="auditTo" label="To" value={filters.to} onChange={(v) => apply({ to: v })} />

          {filtered && (
            <button type="button" onClick={() => apply(EMPTY)} style={{ ...smallButton, height: 40 }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            color: c.danger,
            borderRadius: 12,
            padding: '11px 14px',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : entries.length === 0 ? (
          <Empty>
            {filtered
              ? 'Nothing matches those filters.'
              : 'Nothing recorded yet. Actions appear here as they happen.'}
          </Empty>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Category</Th>
                  <Th style={{ width: '50%' }}>What</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.seq} style={{ borderTop: `1px solid ${c.border}` }}>
                    <Td style={{ whiteSpace: 'nowrap', color: c.muted2 }}>{when(e.createdAt)}</Td>
                    <Td>
                      {/* An entry with no actor is the system, not a gap — signup
                          records one before a user row exists. */}
                      {e.user?.name ?? <span style={{ color: c.faint }}>System</span>}
                    </Td>
                    <Td>
                      <Pill>{AUDIT_CATEGORY_LABEL[e.category as AuditCategory] ?? e.category}</Pill>
                    </Td>
                    <Td style={{ color: c.ink }}>
                      {e.description}
                      <AuditDetail detail={e.detail} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cursor && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            style={{ ...smallButton, height: 40, padding: '0 20px' }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      <p style={{ fontSize: 12, color: c.faint, marginTop: 18, lineHeight: 1.7, maxWidth: 620 }}>
        This record is append-only. Entries cannot be edited or removed. A
        correction is a new entry explaining the old one.
      </p>
    </div>
  );
}

/** Absolute, not "3 hours ago" — an audit trail is read to establish when. */
function when(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The specifics under an entry, where the entry has any worth reading.
 *
 * An audit line that says "applied a stock count" without saying what changed
 * is a record nobody can act on. The API has always stored the specifics in
 * `detail`; this is what finally shows them. Only shapes known to be meant for
 * people are drawn. Anything else in `detail` (ids, internal flags) stays out.
 */
function AuditDetail({ detail }: { detail: Record<string, unknown> | null }) {
  if (!detail) return null;
  const rows: string[] = [];

  // A stock count: one line per product it corrected, with the reason given.
  if (Array.isArray(detail.lines)) {
    for (const raw of detail.lines as Record<string, unknown>[]) {
      const name = String(raw.productName ?? 'A product');
      const system = Number(raw.systemQty);
      const counted = Number(raw.countedQty);
      if (!Number.isFinite(system) || !Number.isFinite(counted)) continue;
      const diff = counted - system;
      const reason =
        typeof raw.reasonLabel === 'string' && raw.reasonLabel
          ? raw.reasonLabel
          : typeof raw.reason === 'string'
            ? ((VARIANCE_REASON_LABEL as Record<string, string>)[raw.reason] ?? raw.reason)
            : null;
      rows.push(
        `${name}: ${system} on record, ${counted} counted (${
          diff < 0 ? `${-diff} missing` : `${diff} extra`
        })${reason ? `. Reason: ${reason}` : ''}`,
      );
    }
  }

  // Logging use from a batch chosen by hand.
  if (typeof detail.chosenBatch === 'string') {
    rows.push(
      detail.passedOverOlder === true
        ? `Taken from batch ${detail.chosenBatch}, chosen by hand, although an older batch was in stock`
        : `Taken from batch ${detail.chosenBatch}, chosen by hand`,
    );
  } else if (Array.isArray(detail.lots)) {
    const lots = (detail.lots as unknown[]).filter((l): l is string => typeof l === 'string');
    if (lots.length > 0) rows.push(`Lot${lots.length === 1 ? '' : 's'}: ${lots.join(', ')}`);
  }

  // A value that was changed: { field: { from, to } }.
  for (const [key, value] of Object.entries(detail)) {
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'from' in value &&
      'to' in value
    ) {
      const { from, to } = value as { from: unknown; to: unknown };
      rows.push(`${humaniseReason(key)}: ${show(from)} changed to ${show(to)}`);
    }
  }

  if (typeof detail.note === 'string' && detail.note.trim() !== '') {
    rows.push(`Note: ${detail.note}`);
  }

  if (rows.length === 0) return null;
  return (
    <ul style={{ listStyle: 'none', margin: '5px 0 0', padding: 0 }}>
      {rows.map((row) => (
        <li key={row} style={{ fontSize: 13, color: c.muted, lineHeight: 1.55 }}>
          {row}
        </li>
      ))}
    </ul>
  );
}

/** A changed value as a person would read it. */
function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'nothing';
  return String(value);
}

function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '12px 16px',
        fontSize: 12.5,
        textTransform: 'lowercase',
        color: c.muted2,
        fontWeight: 400,
        background: c.well,
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <td style={{ padding: '12px 16px', fontSize: 13.5, color: c.muted, ...style }}>{children}</td>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        border: `1px solid ${c.border}`,
        borderRadius: 980,
        padding: '2px 9px',
        fontSize: 11.5,
        color: c.muted2,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '34px 20px', textAlign: 'center', fontSize: 13.5, color: c.muted2 }}>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  color: c.label,
  fontWeight: 500,
  marginBottom: 5,
};

const controlStyle: React.CSSProperties = {
  height: 40,
  padding: '0 10px',
  borderRadius: 12,
  border: `1px solid ${c.borderStrong}`,
  background: c.white,
  color: c.ink,
  fontSize: 13.5,
  fontFamily: sans,
};

function Select({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} style={controlStyle}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function DateBox({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={controlStyle}
      />
    </div>
  );
}

/** Kept for the page heading in the shell. */
export const AUDIT_TITLE = 'Audit log';
